// CC: extracted from integrations.component.ts -> GSuite popup section
import * as util from 'util';
import {Component, EventEmitter, NgZone, OnInit, Output} from "@angular/core";
import {GeneralService} from '../../../../../services/generalService';
import {DICTIONARY} from '../../../../../dictionary';
import {RouteService} from '../../../../../services/routeService';
import {NotificationService} from '../../../../../services/notificationService';
import {ClipboardService} from 'ngx-clipboard';

@Component({
	selector: 'gsuite-integration-component',
	templateUrl: './gsuite-integration.component.html',
})
export class GsuiteIntegrationComponent implements OnInit {
	@Output() connectionChanged = new EventEmitter<void>();
	dic = DICTIONARY;
	planInfo: any;
	userInfo: any;
	integrationActionInProcess = false;
	integImportGroupsSummary = false;
	showGroups = false;
	showAliases = false;
	gsuiteConfigPopup: any;
	gsuiteAdvancedPopup: any;

	// CC: taken from integrations.component.ts -> googleScopes
	googleScopes = `https://www.googleapis.com/auth/admin.directory.group.readonly,
					https://www.googleapis.com/auth/admin.directory.user.readonly,
					https://www.googleapis.com/auth/admin.directory.domain.readonly,
					https://www.googleapis.com/auth/admin.reports.audit.readonly,
					https://www.googleapis.com/auth/contacts.readonly,
					https://www.googleapis.com/auth/contacts.other.readonly,
					https://www.googleapis.com/auth/gmail.modify`;
	googleScopeActivity = 'https://www.googleapis.com/auth/admin.directory.user';
	googleScopesLine = this.googleScopes.split('\n').join('');

	constructor(public gs: GeneralService, private rs: RouteService,
				private ns: NotificationService, private ngZone: NgZone,
				private clipboard: ClipboardService) {}

	ngOnInit() {
		this.userInfo = this.gs.userInfo;
		this.getIntegrations();
	}

	getIntegrations = () => {
		this.rs.getIntegrations().then((planInfo) => {
			this.planInfo = planInfo;
		});
	}

	isConnected = () => {
		return !!this.planInfo?.gsuite_server?.customer_id && this.planInfo?.gsuite_server?.enabled;
	}

	// CC: taken from integrations.component.ts -> connectToGsuite
	connectToGsuite = () => {
		if (this.isConnected()) {
			this.gs.showPopup({
				title: 'Disconnect from Google Workspace',
				subTitle: `Please note - you are about to cancel the connection to your Google Workspace`,
				body: ["Groups will not be automatically updated", "Inbound Shield will be disabled for all mailboxes"],
				type: this.dic.CONSTANTS.popupWarning,
				doneBtnText: 'Disconnect',
				doneCb: () => {
					this.integrationActionInProcess = true;
					let notIdx = this.ns.showInfoMessage('Disconnecting from Google Workspace server...', {timeout: 0});
					this.rs.disconnectGSuite().then(() => {
						this.integrationActionInProcess = false;
						this.ns.overwriteMessage(notIdx, 'Successfully disconnected from Google Workspace server');
						this.getIntegrations();
						this.connectionChanged.emit();
					}, () => {
						this.integrationActionInProcess = false;
					});
				}
			});
		}
		else {
			if (this.planInfo.exchange_server?.tid && this.planInfo.exchange_server?.enabled) {
				this.ns.showErrorMessage(util.format(this.dic.ERRORS.authDisconnectIntegration, 'Exchange Server'));
				return;
			}

			this.gsuiteConfigPopup = {
				gsuiteAdminEmail: '',
				gsuiteCustomerId: '',
				inputErrors: {
					emailInput: false,
					idInput: false
				},
				show: true
			};
		}
	}

	// CC: taken from integrations.component.ts -> connectGSuite
	connectGSuite = () => {
		this.gsuiteConfigPopup.inputErrors = {emailInput: false, idInput: false};

		if (!this.gsuiteConfigPopup.gsuiteCustomerId || !this.gsuiteConfigPopup.gsuiteAdminEmail) {
			this.ns.showWarnMessage(this.dic.ERRORS.fillAllRequired);
			this.gsuiteConfigPopup.inputErrors.emailInput = !!!this.gsuiteConfigPopup.gsuiteAdminEmail;
			this.gsuiteConfigPopup.inputErrors.idInput = !!!this.gsuiteConfigPopup.gsuiteCustomerId;
			return;
		}
		if (!this.gs.validateEmail(this.gsuiteConfigPopup.gsuiteAdminEmail)) {
			this.ns.showWarnMessage(util.format(this.dic.ERRORS.invalidEmail, this.gsuiteConfigPopup.gsuiteAdminEmail));
			this.gsuiteConfigPopup.inputErrors.emailInput = true;
			return;
		}

		this.integrationActionInProcess = true;

		const connectInfo = {
			adminEmail: this.gsuiteConfigPopup.gsuiteAdminEmail,
			customerId: this.gsuiteConfigPopup.gsuiteCustomerId
		};
		this.rs.connectGSuite(connectInfo).then(() => {
			this.gsuiteConfigPopup = null;
			this.ns.showInfoMessage('Successfully connected to Google Workspace server');
			this.getIntegrations();
			this.connectionChanged.emit();

			this.ngZone.run(() => {
				this.integrationActionInProcess = false;
			});
		}, (err) => {
			if (err?.data?.message && !err.data.display_bar) {
				this.ns.showErrorMessage(err.data.message);
			}
			this.integrationActionInProcess = false;
		});
	}

	// CC: taken from integrations.component.ts -> verifyGsuiteIntegration
	verifyGsuiteIntegration = () => {
		this.integrationActionInProcess = true;
		this.rs.verifyGSuite().then((existingScopes) => {
			this.ns.showInfoMessage('Google Workspaces is configured successfully');
			this.planInfo.gsuite_server.scopes = existingScopes;
			this.integrationActionInProcess = false;
		}, (err) => {
			if (err?.data?.message && !err.data.display_bar) {
				this.ns.showErrorMessage(err.data.message);
			}
			this.integrationActionInProcess = false;
		});
	}

	// CC: taken from integrations.component.ts -> verifyGsuiteScope
	verifyGsuiteScope = (scope, showNotification) => {
		this.gsuiteAdvancedPopup.inProcess = true;
		this.rs.verifyGSuite().then((existingScopes) => {
			if (existingScopes.includes(scope)) {
				if (showNotification) {
					this.ns.showInfoMessage('Advanced scope configured successfully');
				}
			}
			this.gsuiteAdvancedPopup = null;
		}, (err) => {
			if (err?.data?.message && !err.data.display_bar) {
				this.ns.showErrorMessage(err.data.message);
			}
			this.gsuiteAdvancedPopup.inProcess = false;
		});
	}

	// CC: taken from integrations.component.ts -> integImportGroups (GSuite case)
	integImportGroups = () => {
		if (!this.isConnected()) {
			return;
		}

		this.integrationActionInProcess = true;
		this.rs.createGSuiteGroups().then(() => {
			this.ns.showInfoMessage('We are updating information from your mail server, this process may take several minutes to finish');
			this.integrationActionInProcess = false;
			this.integImportGroupsSummary = true;
		}, () => {
			this.integrationActionInProcess = false;
		});
	}

	getGroups = (show) => {
		this.showGroups = show;
	}

	getMailboxesAliases = (show) => {
		this.showAliases = show;
	}

	copyText = (text) => {
		this.clipboard.copy(text);
	}
}
